/**
 * timeMatchLayout — fits a Time Match board into the space the screen has left.
 *
 * A board is one question clock plus `count` answer cards. Given the available
 * box (the game area's width x the viewport height left below the info card),
 * this tries both arrangements — question above the answers ("stacked") and
 * question beside them ("side") — over a range of space splits and answer-grid
 * column counts, and keeps the one whose clocks come out largest. Pure (no
 * React/DOM) so it is cheap to re-run on resize and easy to test, just like
 * timeProgression.js.
 *
 * Sizes: an analog clock is measured by its diameter, a digital display by its
 * font size; both in px.
 */

// ---- tunables --------------------------------------------------------------

// Spacing in px. TimeMatchBoard applies these inline, so the math and the
// rendered board always agree. Small screens get the compact set.
export const SPACING = {
  regular: { cardPad: 12, gridGap: 16, sectionGap: 24 },
  compact: { cardPad: 6, gridGap: 10, sectionGap: 14 },
};
export const COMPACT_BELOW = { width: 600, height: 480 };
export const CARD_BORDER = 3; // .clock-option border (TimeMatchForm.css)
export const HOVER_ROOM = 8; // headroom for the hover lift (-4px) and its shadow

// DS-Digital box: em per character (measured worst case 0.452, for "09:05"),
// em padding per side, line height, and border px. Must match
// .digital-clock-display--fluid in TimeMatchForm.css.
export const DIGITAL = { glyph: 0.46, padX: 0.45, padY: 0.4, line: 1, border: 2 };

// [min, max] per clock kind and role. Below the minimum a clock stops being
// legible, so the board falls back to scrolling instead; the maximum keeps a
// huge screen from producing absurd clocks.
export const LIMITS = {
  analog: { question: [150, 600], option: [90, 360] },
  digital: { question: [28, 130], option: [20, 72] },
};

// The search maximises min(question / q, option / o), i.e. it grows the
// question and the answers together in this proportion. Taken from the
// original fixed sizes: a 250px clock over 32px digits, and 48px digits over
// 100px clocks.
const WEIGHTS = {
  analog: { question: 8, option: 1 }, // analog question, digital answers
  digital: { question: 1, option: 2 }, // digital question, analog answers
};

const SHARES = Array.from({ length: 25 }, (_, i) => 0.2 + i * 0.025); // 0.2 .. 0.8
const EPS = 1e-6;

// ---- helpers ---------------------------------------------------------------

// Characters a DigitalClock shows: "HH:MM", plus ":SS", plus " AM".
export function digitalChars({ showSeconds = false, hour24 = false } = {}) {
  return 5 + (showSeconds ? 3 : 0) + (hour24 ? 0 : 3);
}

// Outer box (border included) of a clock of the given size.
export function clockBox(kind, size, chars) {
  if (kind === "analog") return { w: size, h: size };
  const { glyph, padX, padY, line, border } = DIGITAL;
  return {
    w: Math.ceil(size * (chars * glyph + 2 * padX) + 2 * border),
    h: Math.ceil(size * (line + 2 * padY) + 2 * border),
  };
}

// Largest clock size whose box fits a w x h cell (0 if it cannot). A digital
// box keeps 1px in hand for clockBox rounding its size up.
function fitClock(kind, w, h, chars) {
  if (w <= 0 || h <= 0) return 0;
  if (kind === "analog") return Math.min(w, h);
  const { glyph, padX, padY, line, border } = DIGITAL;
  return Math.max(
    0,
    Math.min(
      (w - 2 * border - 1) / (chars * glyph + 2 * padX),
      (h - 2 * border - 1) / (line + 2 * padY)
    )
  );
}

// Best grid of `count` answer cards inside w x h: tries every column count and
// keeps the one with the largest clock.
function fitGrid(kind, count, w, h, chars, spacing) {
  const chrome = 2 * (spacing.cardPad + CARD_BORDER);
  let best = { size: 0, cols: 1 };
  for (let cols = 1; cols <= count; cols++) {
    const rows = Math.ceil(count / cols);
    const cellW = (w - (cols - 1) * spacing.gridGap) / cols - chrome;
    const cellH = (h - HOVER_ROOM - (rows - 1) * spacing.gridGap) / rows - chrome;
    const size = fitClock(kind, cellW, cellH, chars);
    if (size > best.size + EPS) best = { size, cols };
  }
  return best;
}

// ---- main ------------------------------------------------------------------

/**
 * @param {object} p
 * @param {number} p.width  available width (px)
 * @param {number} p.height available height (px)
 * @param {number} p.count  number of answer cards
 * @param {"analog"|"digital"} p.questionKind kind of the question clock; the
 *   answers are the other kind
 * @param {boolean} [p.showSeconds]
 * @param {boolean} [p.hour24]
 * @returns {{arrangement: "stacked"|"side", fits: boolean, spacing: object,
 *   cols: number, gridWidth: number, question: object, option: object}}
 *   `question` / `option` are { kind, size, w, h } (box incl. border; an
 *   answer card adds cardPad + CARD_BORDER per side). `fits` is false when
 *   even the minimum sizes do not fit, and the board will scroll.
 */
export function computeTimeMatchLayout({
  width,
  height,
  count,
  questionKind,
  showSeconds = false,
  hour24 = false,
}) {
  const optionKind = questionKind === "analog" ? "digital" : "analog";
  const chars = digitalChars({ showSeconds, hour24 });
  const n = Math.max(1, count);
  const compact = width < COMPACT_BELOW.width || height < COMPACT_BELOW.height;
  const spacing = compact ? SPACING.compact : SPACING.regular;
  const [qMin, qMax] = LIMITS[questionKind].question;
  const [oMin, oMax] = LIMITS[optionKind].option;
  const weight = WEIGHTS[questionKind];

  let best = null;
  for (const arrangement of ["stacked", "side"]) {
    const along = (arrangement === "stacked" ? height : width) - spacing.sectionGap;
    for (const share of SHARES) {
      const qAlong = along * share;
      const oAlong = along - qAlong;
      const [qw, qh, ow, oh] =
        arrangement === "stacked"
          ? [width, qAlong, width, oAlong]
          : [qAlong, height, oAlong, height];
      const q = Math.min(fitClock(questionKind, qw, qh, chars), qMax);
      const grid = fitGrid(optionKind, n, ow, oh, chars, spacing);
      const o = Math.min(grid.size, oMax);
      const score = Math.min(q / weight.question, o / weight.option);
      const total = q / weight.question + o / weight.option;
      if (
        !best ||
        score > best.score + EPS ||
        (score > best.score - EPS && total > best.total + EPS)
      ) {
        best = { arrangement, q, o, cols: grid.cols, score, total };
      }
    }
  }

  let { arrangement, q, o, cols } = best;
  const fits = q >= qMin && o >= oMin;
  const chrome = 2 * (spacing.cardPad + CARD_BORDER);
  if (!fits) {
    // Too small to play without scrolling: stack at legible sizes and let the
    // page scroll vertically, but never wider than the screen.
    arrangement = "stacked";
    q = Math.min(Math.max(q, qMin), fitClock(questionKind, width, Infinity, chars));
    o = Math.min(Math.max(o, oMin), fitClock(optionKind, width - chrome, Infinity, chars));
    const cardW = clockBox(optionKind, Math.floor(o), chars).w + chrome;
    cols = Math.max(
      1,
      Math.min(n, Math.floor((width + spacing.gridGap) / (cardW + spacing.gridGap)))
    );
  }

  q = Math.floor(q);
  o = Math.floor(o);
  const option = { kind: optionKind, size: o, ...clockBox(optionKind, o, chars) };
  const gridWidth = cols * (option.w + chrome) + (cols - 1) * spacing.gridGap;
  return {
    arrangement,
    fits,
    spacing,
    cols,
    gridWidth,
    question: { kind: questionKind, size: q, ...clockBox(questionKind, q, chars) },
    option,
  };
}
